import { Alert } from '../../atoms/Alert';
import { Button } from '../../atoms/Button';
import { Chip } from '../../atoms/Chip';
import { Icon } from '../../atoms/Icon';

import Player from '../../molecules/Player';
import InnerHTMLContainer from '../../molecules/InnerHTMLContainer';
import { AccuracyDifficulty } from '../../molecules/AccuracyDifficulty';

import Comment from '../comment';
import { ButtonGroup } from '../ButtonGroup';

import { Modal } from '../../templates/Modal';
import { useExerciseListHook } from '../../../hooks/exerciseList';
import {
  ExerciseAction,
  IExercise,
  IExerciseAlternative,
} from '../../../models/exercise';
import ExerciseAlternative from '../../molecules/ExerciseAlternative';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import { ExerciseScribble } from './ExerciseScribble';
import { useUserStore } from '../../../../modules/Auth/stores/authStore';
import { getResolutionVideoConfig } from '../../../utils/video';
import { IVideo } from '../../../models/video';
import { Typography } from '../../molecules/Typography';
import { copyText } from '../../../utils';
import {
  getUniversitarioDisciplinePermission,
} from '../../../utils/discipline';
import { ContentActions } from '../ContentActions';
import LockUniversitario from '../UniversitarioLock';
import { BadgeScore } from '../../atoms/BadgeScore';
import { useShouldShowPopUpScore } from '../../../hooks/useShouldShowPopUpScore';
import {
  getUserPermissions,
  hasExactPermission,
} from '../../../accessControl/utils';

interface ExerciseListProps {
  data: IExercise[];
  isLoading?: boolean;
  tryAgainEnabled?: boolean;
  onExerciseChange: (exercise: IExercise, action?: ExerciseAction) => void;
  badgeInfos?: {
    points: number;
    didPoint: boolean;
    pointAt: string;
    isPendingBadgeScores: boolean;
  } | null;
}

export const ExerciseList = ({
  data,
  tryAgainEnabled = true,
  isLoading = false,
  onExerciseChange,
  badgeInfos,
}: ExerciseListProps) => {
  const {
    showVideo,
    isModalOpen,
    exerciseTypes,
    tryAgain,
    viewAnswer,
    setModalOpen,
    setShowVideo,
    onFavorite,
    setExerciseAlternative,
    convertMathGraphs,
  } = useExerciseListHook();

  const { user } = useUserStore();
  const { shouldShowBadgeScore } = useShouldShowPopUpScore();
  const exerciseContainerRef = useRef<HTMLDivElement>(null);

  const exercise =
    data.filter((exercise: IExercise) => exercise.selected)[0] ??
    ({} as IExercise);

  const resolutionVideo = exercise.resolution_video as IVideo;

  const { playerType, token, hasToken, fallbackToken } =
    getResolutionVideoConfig(resolutionVideo);

  useEffect(() => {
    convertMathGraphs();
  });

  const shouldShowIncorrectAnswer = useMemo(() => {
    if (!exercise.done) return false;
    return !exercise.correct && exercise.exerciseType !== 'discursive';
  }, [exercise]);

  const shouldBadgeScore =
    shouldShowBadgeScore && !badgeInfos?.isPendingBadgeScores;

  const universitarioPermission =
    exercise?.ancestors?.area &&
    getUniversitarioDisciplinePermission(exercise.ancestors.area.slug);

  if (
    universitarioPermission &&
    !hasExactPermission(getUserPermissions(user), universitarioPermission)
  ) {
    return <LockUniversitario />;
  }

  return (
    <div
      key={exercise.id}
      ref={exerciseContainerRef}
      className='relative flex w-full flex-col gap-xxl'
    >
      <div className='flex flex-col gap-lg'>
        <div className='flex h-fit flex-col gap-sm'>
          {user?.exercisePercentage &&
            exercise.exerciseType !== 'discursive' && (
              <AccuracyDifficulty
                correctPercentage={exercise.correctPercentage}
              />
            )}
          <div className='flex flex-col justify-between gap-md md:flex-row md:items-center'>
            <div className='flex items-center gap-sm flex-wrap'>
              <Chip variant='negative'>{`Esta resposta é ${
                exerciseTypes[exercise.exerciseType]
              }`}</Chip>
              {shouldBadgeScore && (
                <BadgeScore
                  score={badgeInfos?.didPoint ? badgeInfos.points : [1, 2]}
                  type={badgeInfos?.didPoint ? 'success' : 'warning'}
                />
              )}
            </div>

            <div className='ml-auto flex items-center gap-md'>
              <ExerciseScribble containerRef={exerciseContainerRef} />
              <ContentActions.Root
                contentType={'Exercise'}
                contentId={exercise.id}
              >
                <ContentActions.FavoriteButton
                  defaultFavorite={exercise.favorite}
                  category={'exercise'}
                  onChange={(isFavorite) => {
                    onExerciseChange(onFavorite(exercise, isFavorite));
                  }}
                />
                <ContentActions.Report />
              </ContentActions.Root>
            </div>
          </div>
        </div>
        <InnerHTMLContainer
          data={exercise.statement}
          type='courseProgram'
          className='hyphens-auto text-mono-color-darkest xs:break-keep'
        />
      </div>
      <div className='flex flex-col gap-md'>
        {exercise.exerciseType === 'discursive' ? (
          <>
            <Alert
              variant='warning'
              message='Desenvolva a questão em seu caderno e depois clique no botão VER RESPOSTA'
            />
            {exercise.showAnswer && (
              <>
                <div className='ml-10 rounded-md border-hairline border-mono-color-darkest p-5'>
                  <InnerHTMLContainer
                    data={exercise.resolution}
                    type='courseProgram'
                    className='text-mono-color-darkest'
                  />
                </div>
                <div className='flex justify-end'>
                  <Button
                    variant='outlined'
                    onClick={() => {
                      onExerciseChange(
                        {
                          ...exercise,
                          correct: true,
                          done: true,
                        },
                        'verifyAnswer'
                      );
                    }}
                  >
                    Marcar como correta
                  </Button>
                </div>
              </>
            )}
          </>
        ) : (
          exercise.alternatives?.map(
            (alternative: IExerciseAlternative, index: number) => {
              const isLastAlternative =
                index === exercise.alternatives.length - 1;
              const shouldShowResolution =
                exercise.exerciseType === 'multiple_answer'
                  ? isLastAlternative
                  : alternative.status === 'correct';

              return (
                <Fragment key={alternative.id}>
                  <div className='flex flex-col'>
                    {exercise.previouslyAnswered &&
                      alternative.status === 'success' && (
                        <span className='flex w-fit items-center gap-xs whitespace-nowrap rounded-t-md bg-feedback-color-success-lightest px-3 py-1 text-sm font-medium text-feedback-color-success-darkest'>
                          <Icon
                            Icon='check_circle'
                            type='material'
                            className='text-base text-feedback-color-success-darkest'
                          />
                          Você já respondeu esta questão!
                        </span>
                      )}
                    <ExerciseAlternative
                      alternativeId={alternative.id}
                      text={alternative.text}
                      value={alternative.text}
                      type={exercise.exerciseType}
                      selected={alternative.selected}
                      status={alternative.status}
                      isLoading={isLoading}
                      disabled={exercise.done}
                      flushTopLeft={
                        exercise.previouslyAnswered &&
                        alternative.status === 'success'
                      }
                      onSelected={() => {
                        onExerciseChange(
                          setExerciseAlternative(alternative.id, exercise),
                          'selectAlternative'
                        );
                      }}
                    />
                  </div>
                  {exercise.showAnswer && shouldShowResolution && (
                    <div className='ml-10 rounded-md border-hairline border-mono-color-medium p-5'>
                      <InnerHTMLContainer
                        data={exercise.resolution}
                        type='courseProgram'
                        className='text-mono-color-darkest'
                      />
                    </div>
                  )}
                </Fragment>
              );
            }
          )
        )}
      </div>
      {(exercise.done ||
        exercise.exerciseType === 'discursive') && (
        <>
          {exercise.correct && (
            <Alert
              title='Resposta correta 😎'
              message='Parabéns!'
              variant='success'
            />
          )}
          {shouldShowIncorrectAnswer && (
            <Alert
              title='Resposta incorreta 😔'
              message='Tente novamente ou veja a resposta correta'
              variant='error'
            />
          )}
          <div className='flex flex-col items-center gap-md md:flex-row md:justify-between'>
            <Button
              allowReadOnly
              LeftIcon={exercise.showAnswer ? 'visibility_off' : 'visibility'}
              variant='text'
              onClick={() => {
                onExerciseChange(viewAnswer(exercise), 'showAnswer');
              }}
            >
              {exercise.showAnswer ? 'Ocultar resposta' : 'Ver resposta'}
            </Button>
            {hasToken && (
              <Button
                allowReadOnly
                LeftIcon='videocam'
                variant='outlined'
                onClick={() => {
                  setShowVideo(true);
                  setModalOpen(true);
                }}
              >
                Assistir resolução em vídeo
              </Button>
            )}
            {tryAgainEnabled && (
              <Button
                LeftIcon='refresh'
                variant='text'
                disabled={exercise.exerciseType === 'discursive'}
                onClick={() => {
                  onExerciseChange(tryAgain(exercise));
                }}
              >
                Tentar novamente
              </Button>
            )}
          </div>
        </>
      )}
      <Modal
        title='Resolução do exercício'
        isOpen={isModalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
      >
        <div className='flex flex-col gap-xl'>
          <ButtonGroup
            buttons={[
              {
                label: 'Resolução em vídeo',
                selected: showVideo,
                onClick: () => setShowVideo(true),
              },
              {
                label: 'Comentários',
                selected: !showVideo,
                onClick: () => setShowVideo(false),
              },
            ]}
          />

          {showVideo ? (
            <Player
              token={token}
              fallbackToken={fallbackToken}
              playerType={playerType}
              initialTime={0}
              className='h-[48vh] w-full'
            />
          ) : (
            <Comment
              title='Comentários da videoaula'
              targetId={exercise.id}
              targetType='Exercise'
              isModal
            />
          )}
        </div>
      </Modal>
    </div>
  );
};
